
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Activity, Link, Link2Off } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { useIsMobile } from '@/hooks/use-mobile';
import { TourButton } from '@/components/common/TourButton';
import { useCTraderAccounts } from '@/hooks/use-ctrader-accounts';

export const EnhancedPageHeader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { data: accounts } = useCTraderAccounts();

  const connectedCount = accounts?.filter(account => account.status === 'connected').length || 0;
  const disconnectedCount = accounts?.filter(account => account.status === 'disconnected').length || 0;
  const totalCount = accounts?.length || 0;

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-muted/20 border border-border/50 rounded-xl p-3 md:p-6 mb-4 md:mb-6">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-40 h-40 bg-primary rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-blue-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
            <Button
              variant="ghost"
              size={isMobile ? "sm" : "icon"}
              onClick={() => navigate('/exchange-accounts')}
              className="hover:bg-primary/10 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
            </Button>
            <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
              <div className={`${isMobile ? 'h-10 w-10' : 'h-14 w-14'} overflow-hidden rounded-xl ${isMobile ? 'border' : 'border-2'} border-primary/20 bg-background shadow-sm flex-shrink-0`}>
                <ImageWithFallback 
                  src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo_ctrader.png" 
                  alt={`${t('ctrader')} logo`}
                  className={`h-full w-full object-contain ${isMobile ? 'p-0.5' : 'p-1'}`} 
                  priority="high"
                  showSkeleton={true}
                  skeletonClassName="bg-muted/50"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className={`${isMobile ? 'text-lg' : 'text-xl md:text-3xl'} font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent truncate`}>
                  {isMobile ? 'cTrader Profiles' : t('manage_ctrader_profiles')}
                </h1>
                {!isMobile && (
                  <p className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2">
                    {t('manage_ctrader_description')}
                  </p>
                )}
              </div>
            </div>
          </div>
          {!isMobile && (
            <div className="flex-shrink-0">
              <TourButton pageId="manage-ctrader" />
            </div>
          )}
        </div>

        {/* Stats Cards Row */}
        {totalCount > 0 && (
          <div className={`grid ${isMobile ? 'grid-cols-3 gap-2' : 'grid-cols-1 md:grid-cols-3 gap-3 md:gap-4'}`}>
            <Card className="bg-gradient-to-r from-background to-background/80 border-border/50 shadow-sm hover:shadow-md transition-shadow">
              <div className={`${isMobile ? 'p-2' : 'p-3 md:p-4'}`}>
                <div className={`flex items-center ${isMobile ? 'flex-col text-center gap-1' : 'gap-3'}`}>
                  <div className={`${isMobile ? 'p-1' : 'p-2'} bg-primary/10 rounded-lg flex-shrink-0`}>
                    <Activity className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-primary`} />
                  </div>
                  <div className={isMobile ? 'flex-1' : ''}>
                    <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground font-medium ${isMobile ? 'truncate' : ''}`}>
                      {isMobile ? 'Total' : 'Total Profiles'}
                    </p>
                    <p className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-foreground`}>{totalCount}</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-gradient-to-r from-background to-background/80 border-border/50 shadow-sm hover:shadow-md transition-shadow">
              <div className={`${isMobile ? 'p-2' : 'p-3 md:p-4'}`}>
                <div className={`flex items-center ${isMobile ? 'flex-col text-center gap-1' : 'gap-3'}`}>
                  <div className={`${isMobile ? 'p-1' : 'p-2'} bg-green-100 rounded-lg flex-shrink-0`}>
                    <Link className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-green-600`} />
                  </div>
                  <div className={isMobile ? 'flex-1' : ''}>
                    <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground font-medium ${isMobile ? 'truncate' : ''}`}>
                      {isMobile ? 'Connected' : 'Connected'}
                    </p>
                    <p className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-foreground`}>{connectedCount}</p>
                  </div>
                </div>
              </div>
            </Card>

            {disconnectedCount > 0 && (
              <Card className="bg-gradient-to-r from-background to-background/80 border-border/50 shadow-sm hover:shadow-md transition-shadow">
                <div className={`${isMobile ? 'p-2' : 'p-3 md:p-4'}`}>
                  <div className={`flex items-center ${isMobile ? 'flex-col text-center gap-1' : 'gap-3'}`}>
                    <div className={`${isMobile ? 'p-1' : 'p-2'} bg-orange-100 rounded-lg flex-shrink-0`}>
                      <Link2Off className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-orange-600`} />
                    </div>
                    <div className={isMobile ? 'flex-1' : ''}>
                      <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground font-medium ${isMobile ? 'truncate' : ''}`}>
                        {isMobile ? 'Disconnected' : 'Disconnected'}
                      </p>
                      <p className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-foreground`}>{disconnectedCount}</p>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
